import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Bell } from "lucide-react";

/**
 * Notifications bell dropdown. Fetches unread count every 60s.
 */
export default function NotificationBell() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  async function load() {
    try {
      const r = await api.get("/notifications");
      setItems(r.data || []);
    } catch (err) {
      // 401 before auth is fine; log everything else for debugging.
      if (err?.response?.status !== 401) {
        console.warn("Notification fetch failed:", err);
      }
    }
  }

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60_000);
    return () => timer.current && clearInterval(timer.current);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  async function markRead(id) {
    setItems((xs) => xs.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await api.post(`/notifications/read/${id}`);
    } catch (err) {
      console.warn("Failed to mark notification read:", err);
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-700"
        title={t("nav.notifications")}
        data-testid="notif-bell"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full
                       bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            role="menu"
            data-testid="notif-dropdown"
            className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-auto bg-white rounded-2xl border border-slate-200 shadow-lg z-40"
          >
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="font-semibold text-slate-900">{t("nav.notifications")}</div>
              <Link
                to="/app/notifications"
                onClick={() => setOpen(false)}
                className="text-xs text-blue-600 font-medium"
                data-testid="notif-view-all"
              >
                {t("notifications.viewAll")}
              </Link>
            </div>
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="notif-empty">
                {t("notifications.empty")}
              </div>
            )}
            {items.slice(0, 8).map((n) => (
              <button
                key={n.id}
                onClick={() => markRead(n.id)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50
                           ${n.read ? "opacity-70" : ""}`}
              >
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 inline-block" />}
                  {n.title}
                </div>
                <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{n.category}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
